/**
 * A procedure's effective parameter set: its own parameters plus, per device
 * requirement, the required definition's — resolved by reference, never copied.
 *
 * Every entry is keyed by the pair `(source, parameterId)`. The two id
 * namespaces are separate, so a procedure's `p-1` and its laser's `p-1` are
 * different parameters and there is nothing to guard against.
 */

export const SELF_PARAMETER_SOURCE = "self";

export interface ParameterDeclaration<TValue> {
  id: string;
  label: string;
  value: TValue;
}

export interface DeviceRequirementRef {
  id: string;
  definitionUrl: string;
}

export interface RequiredDeviceDefinition<TValue> {
  name: string;
  parameters: readonly ParameterDeclaration<TValue>[];
}

export interface EffectiveParameter<TValue> {
  /** `"self"` or the device requirement's member id. */
  source: string;
  /** The required definition's name; absent for the procedure's own parameters. */
  sourceLabel?: string;
  parameterId: string;
  label: string;
  value: TValue;
}

export function effectiveParameters<TValue>(
  own: readonly ParameterDeclaration<TValue>[],
  requirements: readonly DeviceRequirementRef[],
  devices: ReadonlyMap<string, RequiredDeviceDefinition<TValue>>,
): EffectiveParameter<TValue>[] {
  return [
    ...own.map((parameter) => ({
      source: SELF_PARAMETER_SOURCE,
      parameterId: parameter.id,
      label: parameter.label,
      value: parameter.value,
    })),
    ...requirements.flatMap((requirement) => {
      const device = devices.get(requirement.definitionUrl);
      if (!device) return [];
      return device.parameters.map((parameter) => ({
        source: requirement.id,
        sourceLabel: device.name,
        parameterId: parameter.id,
        label: parameter.label,
        value: parameter.value,
      }));
    }),
  ];
}

export function parameterAnswerKey(source: string, parameterId: string): string {
  return `${source}\u0000${parameterId}`;
}

/**
 * Stored answers no declaration accounts for. A device requirement is unpinned,
 * so a kind revision can drop a parameter out from under an answer that is
 * still on the aggregate — it must stay visible rather than vanish from a view
 * that only walks declarations.
 */
export function undeclaredAnswers<TAnswer extends { source: string; parameterId: string }>(
  parameters: readonly EffectiveParameter<unknown>[],
  answers: readonly TAnswer[],
): TAnswer[] {
  const declared = new Set(parameters.map((parameter) => parameterAnswerKey(parameter.source, parameter.parameterId)));
  return answers.filter((answer) => !declared.has(parameterAnswerKey(answer.source, answer.parameterId)));
}
